import {
  CreditCard,
  ShieldAlert,
  SlidersHorizontal,
} from "lucide-react";

import { cn } from "../../../lib/utils";
import { AREA_OF_FRAUD_LABEL_MAP, AreaOfFraud } from "../../../api/cases";
import StepNavigation from "./StepNavigation";

interface FraudTypeCard {
  value: AreaOfFraud;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const FRAUD_TYPE_CARDS: FraudTypeCard[] = [
  {
    value: AreaOfFraud.CREDIT,
    title: "Credit Fraud",
    description: "Cases involving loans, cards, accounts, or credit exposure",
    icon: <CreditCard width={20} height={20} />,
  },
  {
    value: AreaOfFraud.CYBER,
    title: "Cyber Fraud",
    description:
      "Cases involving digital channels or online activity",
    icon: <ShieldAlert width={20} height={20} />,
  },
  {
    value: AreaOfFraud.OPERATIONAL,
    title: "Operational Fraud",
    description:
      "Cases involving direct staff involvement or operational misuse",
    icon: <SlidersHorizontal width={20} height={20} />,
  },
];

export interface StepSelectTypeProps {
  areaOfFraud?: AreaOfFraud;
  onAreaOfFraudChange: (areaOfFraud: AreaOfFraud) => void;
  onNext: () => void;
}

const StepSelectType: React.FC<StepSelectTypeProps> = ({
  areaOfFraud,
  onAreaOfFraudChange,
  onNext,
}) => {
  const handleSelect = (value: AreaOfFraud) => {
    onAreaOfFraudChange(value);
  };

  return (
    <>
      <div className="flex flex-col items-center text-center gap-2 mb-8">
        <h2 className="text-xl md:text-2xl font-semibold text-gray-900">
          Choose the area of fraud to get started
        </h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
        {FRAUD_TYPE_CARDS.map((card) => {
          const selected = areaOfFraud === card.value;
          return (
            <button
              key={card.value}
              type="button"
              onClick={() => handleSelect(card.value)}
              className={cn(
                "text-left rounded-lg border p-4 shadow-sm transition-colors duration-150",
                "hover:border-[#3551F3] hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3551F3]",
                selected
                  ? "border-[#788AF7] bg-[#EBEEFE]"
                  : "border-[#E5E5E5] bg-white hover:border-[#3551F3]",
              )}
              aria-pressed={selected}
              aria-label={`Select ${AREA_OF_FRAUD_LABEL_MAP[card.value]} fraud`}
            >
              <div className="mb-4 flex items-start justify-between gap-3">
                <div className="text-base font-semibold text-gray-900">
                  {card.title}
                </div>
                <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-700">
                  {card.icon}
                </div>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">
                {card.description}
              </p>
            </button>
          );
        })}
      </div>

      <div className="mt-6">
        <StepNavigation
          onNext={onNext}
          disabled={!areaOfFraud}
        />
      </div>
    </>
  );
};

export default StepSelectType;
